import { useMemo, useState } from 'react';
import { differenceInCalendarDays, eachDayOfInterval, format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3, Plus } from 'lucide-react';

import { useCamadaActiva } from '@/lib/queries/camadas';
import {
  useConsumoPienso,
  useCurvaPienso,
  useEntregasDeCamada,
} from '@/lib/queries/entregasPienso';
import { useTiposPienso } from '@/lib/queries/catalogos';
import { fmtFecha, fmtInt, fmtT } from '@/lib/format';
import { crearPuntosEntregaAcumulados, type DeliveryPoint } from '@/lib/piensoChart';
import { useAuthStore } from '@/stores/auth';

interface ChartRow {
  edad: number;
  esperada?: number;
  real?: number;
}

function interpolar(puntos: Array<{ edad: number; valor: number }>, edad: number): number | null {
  if (puntos.length === 0 || edad < puntos[0].edad) return null;
  if (edad >= puntos[puntos.length - 1].edad) return puntos[puntos.length - 1].valor;
  for (let i = 0; i < puntos.length - 1; i += 1) {
    const a = puntos[i];
    const b = puntos[i + 1];
    if (edad >= a.edad && edad <= b.edad) {
      const proporcion = (edad - a.edad) / (b.edad - a.edad || 1);
      return a.valor + proporcion * (b.valor - a.valor);
    }
  }
  return null;
}

/** Pantalla principal de pienso: siempre contextualizada en la camada activa. */
export default function PiensoPage() {
  const [selectedDate, selectDate] = useState<string | null>(null);
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const canManage = user?.rol === 'admin' || user?.rol === 'ganadero';
  const camadaQ = useCamadaActiva();
  const camadaId = camadaQ.data?.id ?? null;
  const entregasQ = useEntregasDeCamada(camadaId);
  const consumoQ = useConsumoPienso(camadaId);
  const curvaQ = useCurvaPienso(camadaId);
  const tiposQ = useTiposPienso();

  const tipoPorId = useMemo(
    () => new Map((tiposQ.data ?? []).map((tipo) => [tipo.id, tipo.codigo])),
    [tiposQ.data],
  );
  const entregas = useMemo(
    () => [...(entregasQ.data ?? [])].sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id),
    [entregasQ.data],
  );
  const entregasAsc = useMemo(() => [...entregas].reverse(), [entregas]);

  const puntosEntrega = useMemo<DeliveryPoint[]>(
    () =>
      camadaQ.data ? crearPuntosEntregaAcumulados(entregasAsc, camadaQ.data.fecha_entrada) : [],
    [camadaQ.data, entregasAsc],
  );

  const chartData = useMemo<ChartRow[]>(() => {
    const camada = camadaQ.data;
    if (!camada) return [];
    const esperada = (curvaQ.data?.puntos ?? []).map((punto) => ({
      edad: punto.edad_dias,
      valor: punto.kg_acumulados,
    }));
    const edadActual = Math.max(
      differenceInCalendarDays(new Date(), parseISO(camada.fecha_entrada)) + 1,
      1,
    );
    const edadMax = Math.max(edadActual, esperada.at(-1)?.edad ?? 0, puntosEntrega.at(-1)?.x ?? 0);
    const edadMin = Math.min(esperada[0]?.edad ?? edadActual, puntosEntrega[0]?.dia ?? edadActual);
    const filas: ChartRow[] = [];

    // Acotar sólo la interpolación visual; conservar puntos fuente y cada entrega.
    const paso = Math.max(1, Math.ceil((edadMax - edadMin) / 398));
    const edades = new Set(esperada.map((punto) => punto.edad));
    for (let edad = edadMin; edad <= edadMax; edad += paso) edades.add(edad);
    if (paso > 1) edades.add(edadActual);
    for (const edad of edades) {
      const esperado = interpolar(esperada, edad);
      if (esperado == null) continue;
      filas.push({ edad, esperada: esperado / 1000 });
    }
    for (const punto of puntosEntrega) {
      const esperado = interpolar(esperada, punto.x);
      filas.push({
        edad: punto.x,
        ...(esperado == null ? {} : { esperada: esperado / 1000 }),
        real: punto.acumulado / 1000,
      });
    }
    return filas.sort((a, b) => a.edad - b.edad);
  }, [camadaQ.data, curvaQ.data, puntosEntrega]);

  const comparisonRows = useMemo(() => {
    const camada = camadaQ.data;
    if (!camada) return [];
    const start = parseISO(camada.fecha_entrada);
    const lastDelivery = entregasAsc.at(-1)?.fecha;
    const end = new Date(
      Math.max(
        start.getTime(),
        new Date().getTime(),
        lastDelivery ? parseISO(lastDelivery).getTime() : 0,
      ),
    );
    const expected = (curvaQ.data?.puntos ?? []).map((p) => ({
      edad: p.edad_dias,
      valor: p.kg_acumulados,
    }));
    let delivered = 0;
    let deliveryIndex = 0;
    return eachDayOfInterval({ start, end }).map((date) => {
      const fecha = format(date, 'yyyy-MM-dd');
      while (deliveryIndex < entregasAsc.length && entregasAsc[deliveryIndex].fecha <= fecha) {
        delivered += Number(entregasAsc[deliveryIndex].kg);
        deliveryIndex += 1;
      }
      const age = differenceInCalendarDays(date, start) + 1;
      return {
        fecha,
        age,
        actual: entregasQ.data == null ? null : delivered,
        reference: interpolar(expected, age),
      };
    });
  }, [camadaQ.data, entregasAsc, entregasQ.data, curvaQ.data]);

  if (camadaQ.isLoading) {
    return (
      <main className="mx-auto max-w-md px-4 py-6 text-sm text-text-secondary">
        {t('common.loading')}
      </main>
    );
  }
  if (camadaQ.isError) {
    return (
      <main className="mx-auto max-w-md px-4 py-6">
        <p role="alert" className="text-sm text-danger">
          {t('common.error')}
        </p>
      </main>
    );
  }
  if (!camadaQ.data) {
    return (
      <main className="mx-auto max-w-md px-4 py-8">
        <div className="rounded-xl border border-border bg-surface p-6 text-center">
          <BarChart3 className="mx-auto mb-3 h-10 w-10 text-text-muted" aria-hidden />
          <h1 className="text-xl font-semibold text-text-primary">{t('pienso.modulo_titulo')}</h1>
          <p className="mt-2 text-sm text-text-secondary">{t('pienso.error_sin_camada')}</p>
          {canManage && (
            <Link
              to="/camadas/nueva"
              className="mt-4 inline-flex min-h-11 items-center rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-contrast hover:opacity-90"
            >
              {t('pienso.crear_camada')}
            </Link>
          )}
        </div>
      </main>
    );
  }

  const camada = camadaQ.data;
  const canEdit = canManage && !camada.cerrada;
  const cargandoDatos =
    entregasQ.isLoading || consumoQ.isLoading || curvaQ.isLoading || tiposQ.isLoading;
  const datosError = entregasQ.isError || consumoQ.isError || curvaQ.isError || tiposQ.isError;
  const entregasDisponibles = entregasQ.data != null;
  const totalKg = consumoQ.data?.kg_total_entregado ?? null;
  const ultimaEntrega = entregas[0] ?? null;
  const entregasVisibles = entregas.slice(0, 3);
  const labelCurva =
    curvaQ.data?.origen === 'historico'
      ? t('pienso.curva_historica', { n: curvaQ.data.camadas_historico })
      : t('pienso.curva_ajustes');
  const selected = comparisonRows.find((p) => p.fecha === selectedDate) ?? comparisonRows.at(-1);
  const displayKg = (value: number | null) => (value == null ? 'Sin datos' : `${fmtInt(value)} kg`);

  return (
    <main className="ui-page pt-2" data-testid="pienso-page">
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ui-page-title">{t('pienso.modulo_titulo')}</h1>
          <p className="mt-1 break-words text-sm text-text-secondary">
            {camada.codigo_lote} · {t('pienso.camada_activa')}
          </p>
        </div>
        {canEdit && (
          <Link
            to={`/camadas/${camada.id}/entregas-pienso/nueva?from=pienso`}
            className="ui-primary inline-flex shrink-0 items-center gap-1.5"
            data-testid="pienso-cta-cargar"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('pienso.card_cta_cargar')}
          </Link>
        )}
      </header>

      {datosError && (
        <div role="alert" className="parte-error mb-4">
          No se pudieron actualizar todos los datos de pienso. Sólo se muestran los datos
          disponibles.
          <button
            type="button"
            className="ui-secondary mt-3 w-full"
            onClick={() => {
              void entregasQ.refetch();
              void consumoQ.refetch();
              void curvaQ.refetch();
              void tiposQ.refetch();
            }}
          >
            Reintentar
          </button>
        </div>
      )}

      {cargandoDatos ? (
        <p className="text-sm text-text-secondary">{t('common.loading')}</p>
      ) : (
        <>
          <section className="mb-3 grid grid-cols-2 gap-2" aria-label={t('pienso.resumen_titulo')}>
            <div className="min-w-0 rounded-xl border border-border bg-surface p-3">
              <p className="text-xs text-text-secondary">{t('pienso.total_entregado')}</p>
              <p className="mt-1 text-xl font-bold tabular-nums text-text-primary">
                {totalKg == null ? '—' : fmtT(totalKg)}
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                {entregasDisponibles
                  ? t('pienso.entregas_count', { count: entregas.length })
                  : 'Entregas no disponibles'}
              </p>
            </div>
            <div className="min-w-0 rounded-xl border border-border bg-surface p-3">
              <p className="text-xs text-text-secondary">{t('pienso.ultima_entrega')}</p>
              <p className="mt-1 break-words text-lg font-semibold leading-6 text-text-primary">
                {ultimaEntrega ? fmtFecha(ultimaEntrega.fecha) : '—'}
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                {!entregasDisponibles
                  ? 'Entregas no disponibles'
                  : ultimaEntrega
                    ? `${fmtInt(ultimaEntrega.kg)} kg`
                    : t('pienso.sin_entregas')}
              </p>
            </div>
          </section>

          {chartData.length > 0 ? (
            <section
              className="mb-3 rounded-xl border border-border bg-surface p-3"
              data-testid="pienso-grafico"
            >
              <ul
                className="mb-2 flex flex-wrap items-center gap-3 text-xs text-text-secondary"
                data-testid="pienso-leyenda"
              >
                {curvaQ.data?.puntos?.length ? (
                  <li className="flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className="inline-block h-0.5 w-5 rounded"
                      style={{ borderTop: '2px dashed var(--c-text-secondary)' }}
                    />
                    <span>{labelCurva}</span>
                  </li>
                ) : null}
                {entregas.length ? (
                  <li className="flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className="inline-block h-0.5 w-5 rounded"
                      style={{ backgroundColor: 'var(--c-ok-text)' }}
                    />
                    <span>{t('pienso.curva_real')}</span>
                  </li>
                ) : null}
              </ul>
              {selected && (
                <>
                  <label className="nave-caption nave-chart-date" htmlFor="pienso-chart-date">
                    Consultar fecha
                    <select
                      id="pienso-chart-date"
                      className="ui-field"
                      value={selected.fecha}
                      onChange={(e) => selectDate(e.target.value)}
                    >
                      {comparisonRows.map((p) => (
                        <option key={p.fecha} value={p.fecha}>
                          {fmtFecha(p.fecha)} · Día {p.age}
                        </option>
                      ))}
                    </select>
                  </label>
                  <section
                    aria-label="Comparación de pienso por fecha"
                    className="nave-chart-values"
                    aria-live="polite"
                  >
                    <div className="min-w-0 break-words">
                      <span>Entregado acumulado</span>
                      <strong>{displayKg(selected.actual)}</strong>
                    </div>
                    <div className="min-w-0 break-words">
                      <span>{labelCurva}</span>
                      <strong>{displayKg(selected.reference)}</strong>
                    </div>
                  </section>
                </>
              )}
              <ResponsiveContainer width="100%" height={170}>
                <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                  <XAxis
                    dataKey="edad"
                    type="number"
                    tick={{ fontSize: 10, fill: 'var(--c-text-secondary)' }}
                    tickFormatter={(value) => `D${value}`}
                  />
                  <YAxis
                    width={40}
                    tick={{ fontSize: 10, fill: 'var(--c-text-secondary)' }}
                    tickFormatter={(value) => `${value}t`}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      const numeric = Number(value);
                      if (!Number.isFinite(numeric)) return ['', ''];
                      return [
                        `${numeric.toFixed(2).replace('.', ',')} t`,
                        name === 'esperada' ? labelCurva : t('pienso.curva_real'),
                      ];
                    }}
                    labelFormatter={(label) => {
                      const x = Number(label);
                      const puntoEntrega = puntosEntrega.find(
                        (punto) => Math.abs(punto.x - x) < 0.001,
                      );
                      return t('pienso.dia', { n: puntoEntrega?.dia ?? Math.round(x) });
                    }}
                    contentStyle={{
                      backgroundColor: 'var(--c-surface)',
                      border: '1px solid var(--c-border)',
                      borderRadius: '0.5rem',
                      color: 'var(--c-text-primary)',
                    }}
                  />
                  {curvaQ.data?.puntos?.length ? (
                    <Line
                      type="monotone"
                      dataKey="esperada"
                      stroke="var(--c-text-secondary)"
                      strokeDasharray="5 4"
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ) : null}
                  {entregas.length ? (
                    <Line
                      type="monotone"
                      dataKey="real"
                      stroke="var(--c-ok-text)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ) : null}
                  {puntosEntrega.map((punto, index) => (
                    <ReferenceDot
                      key={`${punto.dia}-${index}`}
                      x={punto.x}
                      y={punto.acumulado / 1000}
                      r={4}
                      fill="var(--c-ok-text)"
                      stroke="var(--c-surface)"
                      strokeWidth={2}
                      ifOverflow="extendDomain"
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <details className="nave-disclosure">
                <summary>Ver valores por fecha</summary>
                <table className="nave-table">
                  <caption className="sr-only">Pienso acumulado por fecha</caption>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Entregado acumulado</th>
                      <th>{labelCurva}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map((p) => (
                      <tr key={p.fecha}>
                        <th scope="row">
                          {fmtFecha(p.fecha)} · Día {p.age}
                        </th>
                        <td>{displayKg(p.actual)}</td>
                        <td>{displayKg(p.reference)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </section>
          ) : (
            <p className="mb-5 rounded-xl border border-dashed border-border p-5 text-center text-sm text-text-secondary">
              {t('pienso.grafica_sin_datos')}
            </p>
          )}

          <section aria-labelledby="pienso-entregas-heading">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 id="pienso-entregas-heading" className="font-semibold text-text-primary">
                {t('pienso.entregas_titulo')}
              </h2>
              <span className="text-xs text-text-secondary">
                {entregasDisponibles ? t('pienso.entregas_count', { count: entregas.length }) : '—'}
              </span>
            </div>
            {!entregasDisponibles ? (
              <p className="cm-form-note">Entregas no disponibles.</p>
            ) : entregas.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-5 text-center">
                <p className="text-sm text-text-secondary">{t('pienso.sin_entregas')}</p>
                {canEdit && (
                  <Link
                    to={`/camadas/${camada.id}/entregas-pienso/nueva?from=pienso`}
                    className="mt-3 inline-block text-sm font-medium text-accent underline"
                  >
                    {t('pienso.card_cta_cargar')}
                  </Link>
                )}
              </div>
            ) : (
              <>
                <ul className="divide-y divide-border" data-testid="pienso-listado">
                  {entregasVisibles.map((entrega) => {
                    const contenido = (
                      <>
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="font-medium text-text-primary">{fmtFecha(entrega.fecha)}</p>
                          <p className="shrink-0 font-semibold tabular-nums text-text-primary">
                            {fmtInt(entrega.kg)} kg
                          </p>
                        </div>
                        <p className="mt-1 text-xs text-text-secondary">
                          {tipoPorId.get(entrega.piensotipo_id) ??
                            t('camada_detalle.pienso_tipo_no_disponible')}
                          {entrega.num_albaran_procavi && ` · ${entrega.num_albaran_procavi}`}
                        </p>
                      </>
                    );
                    return (
                      <li key={entrega.id}>
                        {canEdit ? (
                          <Link
                            to={`/camadas/${camada.id}/entregas-pienso/nueva?id=${entrega.id}&from=pienso`}
                            className="block min-h-12 py-3 transition-colors hover:bg-background"
                            aria-label={`${t('pienso.titulo_editar')}: ${fmtFecha(entrega.fecha)}`}
                          >
                            {contenido}
                          </Link>
                        ) : (
                          <div className="min-h-12 py-3">{contenido}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {entregas.length > 3 && (
                  <Link
                    to={`/camadas/${camada.id}/entregas-pienso?from=pienso`}
                    className="mt-3 flex min-h-11 w-full items-center justify-center rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface"
                    data-testid="pienso-ver-todas"
                  >
                    {t('camada_detalle.ver_todas_piensos', { n: entregas.length })}
                  </Link>
                )}
              </>
            )}
          </section>
        </>
      )}
    </main>
  );
}
